// Cardumen de ojos: port a canvas del motor de los reels (descript/ojos/render_v21.py).
// La física corre en "unidades de reel" (el cuadro de 1080 px del video, sprite de 66 px) a 30 pasos
// por segundo, igual que el render de video; se dibuja interpolando para que se vea fluido a 60/120 Hz.
// Reglas: separación + alineación + cohesión (peces), órbita con remolino alrededor del objetivo,
// susto en un punto, rodean los elementos [data-avoid] sin tocarlos, miran lo más cercano,
// parpadean solos y en ola, y los que quedan trabados se desvanecen y reaparecen en un lugar libre.
import { useEffect, useRef } from 'react';

const INK_EYES = [1, 2, 3, 4, 5, 6, 7, 8].map((i) => `/eye${i}.png`);
const RED_EYES = [2, 3, 4, 5, 6].map((i) => `/eye${i}-red.png`);
const BLINK = [1.0, 0.6, 0.3, 0.12];
const STEP = 1 / 30;
const SPR_REEL = 66;

let spritePromise = null;
function loadSprites() {
  if (!spritePromise) {
    const load = (src) =>
      new Promise((resolve) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => resolve(null);
        im.src = src;
      });
    spritePromise = Promise.all([Promise.all(INK_EYES.map(load)), Promise.all(RED_EYES.map(load))]).then(
      ([ink, red]) => ({ ink: ink.filter(Boolean), red: red.filter(Boolean) })
    );
  }
  return spritePromise;
}

// generador pseudoaleatorio con semilla: el cardumen arranca igual en cada visita
function mulberry(seed) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

class School {
  constructor(canvas, container, opts, sprites) {
    this.canvas = canvas;
    this.container = container;
    this.o = opts;
    this.sprites = sprites;
    this.rng = mulberry(opts.seed || 12);
    this.pointer = null;       // {x, y, t} en unidades de reel
    this.startles = [];        // {x, y, n}
    this.agit = 0;
    this.t = 0;
    this.rects = [];
    this.nextGroupBlink = 3 + this.rng() * 3;
    this.measure();
    this.spawn();
  }

  gauss() {
    const u = 1 - this.rng(), v = this.rng();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  gamma4() { // Gamma(4, 1): suma de 4 exponenciales
    let s = 0;
    for (let k = 0; k < 4; k++) s -= Math.log(1 - this.rng());
    return s;
  }

  measure() {
    const r = this.container.getBoundingClientRect();
    this.cw = r.width; this.ch = r.height;
    const spr = clamp(this.cw * this.o.spriteRatio, this.o.spriteMin, this.o.spriteMax);
    this.S = spr / SPR_REEL;                   // css px por unidad de reel
    this.W = this.cw / this.S; this.H = this.ch / this.S;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(this.cw * dpr);
    this.canvas.height = Math.round(this.ch * dpr);
    this.canvas.style.width = this.cw + 'px';
    this.canvas.style.height = this.ch + 'px';
    this.dpr = dpr;
    this.readRects();
  }

  readRects() {
    const base = this.container.getBoundingClientRect();
    const S = this.S, pad = this.o.avoidPad / S;
    // scope: dónde buscar obstáculos y objetivo (por defecto, el mismo contenedor del canvas)
    const scope = (this.o.scopeSelector && document.querySelector(this.o.scopeSelector)) || this.container;
    this.rects = Array.from(scope.querySelectorAll('[data-avoid]')).map((el) => {
      const b = el.getBoundingClientRect();
      return {
        l: (b.left - base.left) / S - pad, t: (b.top - base.top) / S - pad,
        r: (b.right - base.left) / S + pad, b: (b.bottom - base.top) / S + pad,
      };
    }).filter((q) => q.r > q.l && q.b > q.t);
    const tel = this.o.targetSelector && scope.querySelector(this.o.targetSelector);
    // al acecho: sin objetivo a la vista, esperan fuera del marco; cuando aparece uno, entran de golpe
    const was = this.lurking;
    this.lurking = !!(this.o.lurk && !tel);
    if (was !== undefined && was !== this.lurking) this.agit = 1;
    if (tel) {
      const b = tel.getBoundingClientRect();
      this.home = [((b.left + b.right) / 2 - base.left) / S, ((b.top + b.bottom) / 2 - base.top) / S];
      // como la cabeza en los reels (hw + 90): el remolino va por fuera del bloque, no aplastado contra él
      this.homeR = Math.min(b.width, b.height) / 2 / S;
    } else {
      this.home = [this.W / 2, this.H / 2];
      this.homeR = 0;
    }
    if (!this.target) this.target = this.home.slice();
  }

  // distancia con signo al obstáculo más cercano y dirección para alejarse
  field(x, y) {
    let best = 1e9, ax = 0, ay = 0;
    for (const q of this.rects) {
      const cx = clamp(x, q.l, q.r), cy = clamp(y, q.t, q.b);
      let d, gx, gy;
      if (cx !== x || cy !== y) {
        gx = x - cx; gy = y - cy; d = Math.hypot(gx, gy) || 1e-6; gx /= d; gy /= d;
      } else {
        const dl = x - q.l, dr = q.r - x, dt = y - q.t, db = q.b - y;
        const m = Math.min(dl, dr, dt, db);
        d = -m;
        if (m === dl) { gx = -1; gy = 0; } else if (m === dr) { gx = 1; gy = 0; }
        else if (m === dt) { gx = 0; gy = -1; } else { gx = 0; gy = 1; }
      }
      if (d < best) { best = d; ax = gx; ay = gy; }
    }
    return [best, ax, ay];
  }

  freeSpot(j) {
    for (let k = 0; k < 80; k++) {
      const x = 40 + this.rng() * (this.W - 80), y = 120 + this.rng() * Math.max(10, this.H - 240);
      if (this.rects.length && this.field(x, y)[0] < this.rad[j] + 30) continue;
      let ok = true;
      for (let i = 0; i < this.n && ok; i++) {
        if (i !== j && Math.hypot(this.px[i] - x, this.py[i] - y) - this.rad[i] - this.rad[j] < 4) ok = false;
      }
      if (ok) return [x, y];
    }
    return null;
  }

  spawn() {
    const area = this.W * this.H;
    const n = Math.round(clamp((area / (1080 * 1920)) * 170 * this.o.density, this.o.minEyes, this.o.maxEyes));
    this.n = n;
    const F = () => new Float32Array(n);
    Object.assign(this, {
      px: F(), py: F(), vx: F(), vy: F(), hx: F(), hy: F(), size: F(), rad: F(), phase: F(), spin: F(),
      orbit: F(), ang: F(), flip: F(), alpha: F(), atgt: F(), rev: F(), pdx: F(), pdy: F(),
      ppx: F(), ppy: F(), lx: F(), ly: F(), resid: F(), blinkAt: F(), idx: new Uint8Array(n), red: new Uint8Array(n),
    });
    for (let j = 0; j < n; j++) {
      this.size[j] = clamp(this.gamma4() * 0.17 + 0.35, 0.4, 1.35);
      this.rad[j] = SPR_REEL * this.size[j] * 0.5 + 4;
      this.red[j] = this.rng() < this.o.redRatio && this.sprites.red.length ? 1 : 0;
      this.idx[j] = Math.floor(this.rng() * (this.red[j] ? this.sprites.red.length : this.sprites.ink.length));
      const a0 = this.rng() * Math.PI * 2;
      this.vx[j] = Math.cos(a0) * 3; this.vy[j] = Math.sin(a0) * 3;
      this.hx[j] = Math.cos(a0); this.hy[j] = Math.sin(a0);
      this.phase[j] = this.rng() * 6.28;
      this.spin[j] = this.rng() < 0.85 ? 1 : -1;       // la mayoría gira igual; algunos a contramano
      this.orbit[j] = (130 + this.rng() * 390) * this.o.orbitScale; // cada pez su radio: remolino, no grumo
      this.ang[j] = a0 * 180 / Math.PI;
      this.flip[j] = Math.cos(a0) >= 0 ? 1 : -1;
      this.alpha[j] = 0; this.atgt[j] = 1;
      this.blinkAt[j] = this.rng() * 5;
    }
    this.park = F();                                   // ángulo de su lugar de espera fuera del marco
    for (let j = 0; j < n; j++) {
      // parkSides: esperan a los costados (entran por los márgenes, sin cruzar el texto de arriba o de abajo)
      this.park[j] = this.o.parkSides ? (this.rng() < 0.5 ? 0 : Math.PI) + (this.rng() - 0.5) * 1.1 : this.rng() * Math.PI * 2;
      const s = this.o.lurk ? this.parkAt(j) : this.freeSpot(j) || [this.rng() * this.W, this.rng() * this.H];
      this.px[j] = s[0]; this.py[j] = s[1];
      if (this.o.lurk) this.alpha[j] = 1;
    }
    this.ppx.set(this.px); this.ppy.set(this.py);
    this.lx.set(this.px); this.ly.set(this.py);
    // entrada escalonada: aparecen de a poco, de los bordes hacia el centro
    this.fadeDelay = new Float32Array(n);
    for (let j = 0; j < n; j++) {
      const d = Math.hypot(this.px[j] - this.W / 2, this.py[j] - this.H / 2) / Math.hypot(this.W / 2, this.H / 2);
      this.fadeDelay[j] = (1 - d) * 0.9 + this.rng() * 0.3;
    }
  }

  // lugar de espera: sobre un rectángulo que rodea el marco por fuera (en diagonal también queda afuera)
  parkAt(j) {
    const a = this.park[j], m = 140 + this.rad[j] * 2, c = Math.cos(a), s = Math.sin(a);
    const k = Math.min((this.W / 2 + m) / (Math.abs(c) || 1e-6), (this.H / 2 + m) / (Math.abs(s) || 1e-6));
    return [this.W / 2 + c * k, this.H / 2 + s * k];
  }

  resize() {
    const oldS = this.S, oldW = this.W, oldH = this.H;
    this.measure();
    const k = oldS / this.S;
    for (let j = 0; j < this.n; j++) {
      this.px[j] = clamp(this.px[j] * k, 0, this.W); this.py[j] = clamp(this.py[j] * k, 0, this.H);
      this.ppx[j] = this.px[j]; this.ppy[j] = this.py[j];
    }
    this.target = this.home.slice();
    if (Math.abs(this.W * this.H - oldW * oldH) / (oldW * oldH) > 0.35) this.spawn();
  }

  startle(cssX, cssY) {
    this.startles.push({ x: cssX / this.S, y: cssY / this.S, n: 5 });
    this.agit = 1;
  }

  setPointer(cssX, cssY) {
    this.pointer = { x: cssX / this.S, y: cssY / this.S, t: this.t };
  }

  step() {
    const n = this.n, { px, py, vx, vy, rad } = this;
    this.t += STEP;
    this.agit *= 0.93;
    const agit = this.agit;
    const fx = new Float32Array(n), fy = new Float32Array(n);
    const ptr = this.pointer && this.t - this.pointer.t < 2.5 ? this.pointer : null;
    const tgt = ptr ? [ptr.x, ptr.y] : this.home;
    this.target[0] += (tgt[0] - this.target[0]) * 0.25;
    this.target[1] += (tgt[1] - this.target[1]) * 0.25;
    const TX = this.target[0], TY = this.target[1];

    for (let i = 0; i < n; i++) {
      let sx = 0, sy = 0, cnt = 0, svx = 0, svy = 0, spx = 0, spy = 0;
      for (let j = 0; j < n; j++) {
        if (j === i) continue;
        const dx = px[i] - px[j], dy = py[i] - py[j];
        const d2 = dx * dx + dy * dy;
        if (d2 > 160000) continue;
        const dist = Math.sqrt(d2) + 1e-6;
        const sep = (rad[i] + rad[j]) * 1.25;
        if (dist < sep) {
          const k = Math.pow((sep - dist) / sep, 1.5);
          sx += (dx / dist) * k; sy += (dy / dist) * k;
        }
        if (dist < 120) { cnt++; svx += vx[j]; svy += vy[j]; spx += px[j]; spy += py[j]; }
      }
      let f0 = sx * 2, f1 = sy * 2;
      if (cnt > 0) {                                   // alineación y cohesión: esto los hace peces
        f0 += (svx / cnt - vx[i]) * 0.07 + (spx / cnt - px[i]) * 0.0008;
        f1 += (svy / cnt - vy[i]) * 0.07 + (spy / cnt - py[i]) * 0.0008;
      }
      if (this.lurking) {                              // al acecho: cada uno a su lugar fuera del marco
        const [qx, qy] = this.parkAt(i), ex = qx - px[i], ey = qy - py[i], a = Math.hypot(ex, ey) + 1e-6;
        const pull = clamp(a * 0.004, 0, 0.7);
        f0 += (ex / a) * pull; f1 += (ey / a) * pull;
      } else {
        // giran alrededor del objetivo
        const ex = TX - px[i], ey = TY - py[i], a = Math.hypot(ex, ey) + 1e-6, ux = ex / a, uy = ey / a;
        const orb = ptr ? 60 + (this.orbit[i] - 130) * 0.35
          : this.homeR ? this.homeR + 90 + (this.orbit[i] - 130) * 0.5 : this.orbit[i];
        const pull = clamp((a - orb) * 0.004, -0.5, 0.7);
        const sw = 0.42 * Math.exp(-a / 800) * this.spin[i];
        f0 += ux * pull - uy * sw; f1 += uy * pull + ux * sw;
      }
      // susto: los cercanos se espantan
      for (const s of this.startles) {
        const qx = px[i] - s.x, qy = py[i] - s.y, qa = Math.hypot(qx, qy) + 1e-6;
        const k = clamp(1 - qa / 230, 0, 1) * (3 + 3 * agit);
        f0 += (qx / qa) * k; f1 += (qy / qa) * k;
      }
      f0 += this.gauss() * 0.12; f1 += this.gauss() * 0.12;
      if (!this.o.lurk) {                              // con modo al acecho no hay paredes: entran y salen del marco
        const m = 20, top = m + 60 + this.o.topPad / this.S;
        if (px[i] < m) f0 += 0.6; if (px[i] > this.W - m) f0 -= 0.6;
        if (py[i] < top) f1 += 0.6; if (py[i] > this.H - m) f1 -= 0.6;
      }
      if (this.rects.length) {
        const [d, gx, gy] = this.field(px[i], py[i]);
        const pen = rad[i] + 14 - d;
        if (pen > 0) { const k = clamp(0.8 + pen * 0.12, 0, 4); f0 += gx * k; f1 += gy * k; }
      }
      fx[i] = f0; fy[i] = f1;
    }
    for (const s of this.startles) s.n--;
    this.startles = this.startles.filter((s) => s.n > 0);

    const sp0 = this.o.speed || 1;
    const gain = (0.45 + 0.35 * agit) * sp0, vmax = (5.5 + 3.5 * agit) * sp0;
    this.ppx.set(px); this.ppy.set(py);
    for (let i = 0; i < n; i++) {
      vx[i] = vx[i] * 0.96 + fx[i] * gain; vy[i] = vy[i] * 0.96 + fy[i] * gain;
      const sp = Math.hypot(vx[i], vy[i]) + 1e-9, k = clamp(sp, 2.0, vmax) / sp; // un pez nunca se queda quieto
      vx[i] *= k; vy[i] *= k;
      px[i] += vx[i]; py[i] += vy[i];
    }
    for (let pass = 0; pass < 2; pass++) {          // nadie se encima: corrección de posición entre pares
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          const dx = px[i] - px[j], dy = py[i] - py[j], lim = (rad[i] + rad[j]) * 1.05;
          if (Math.abs(dx) > lim || Math.abs(dy) > lim) continue;
          const dist = Math.hypot(dx, dy) + 1e-6, pen = lim - dist;
          if (pen > 0) {
            const k = (pen * 0.25) / dist;
            px[i] += dx * k; py[i] += dy * k; px[j] -= dx * k; py[j] -= dy * k;
          }
        }
      }
    }
    for (let i = 0; i < n; i++) {                    // rodean el texto: nunca lo pisan (tope 14 por paso)
      this.resid[i] = 0;
      if (!this.rects.length) continue;
      let moved = 0;
      for (let it = 0; it < 4; it++) {
        const [d, gx, gy] = this.field(px[i], py[i]);
        const pen = rad[i] - d;
        if (pen <= 0 || moved >= 14) break;
        const st = Math.min(pen, 30, 14 - moved);
        px[i] += gx * st; py[i] += gy * st; moved += st;
        const vin = vx[i] * gx + vy[i] * gy;
        if (vin < 0) { vx[i] -= gx * vin; vy[i] -= gy * vin; }
      }
      this.resid[i] = rad[i] - this.field(px[i], py[i])[0];
    }
    for (let i = 0; i < n; i++) {
      const sp = Math.hypot(vx[i], vy[i]) + 1e-9;
      let hx = this.hx[i] * 0.8 + (vx[i] / sp) * 0.2, hy = this.hy[i] * 0.8 + (vy[i] / sp) * 0.2;
      const hn = Math.hypot(hx, hy) + 1e-9;
      this.hx[i] = hx / hn; this.hy[i] = hy / hn;
    }
    this.look(ptr);
    this.lifecycle();
  }

  // mirada: al puntero si hay; si no, al centro del texto más cercano; giro suave y con histéresis
  look(ptr) {
    const { px, py } = this;
    for (let j = 0; j < this.n; j++) {
      let want;
      if (ptr) want = Math.atan2(ptr.y - py[j], ptr.x - px[j]);
      else if (this.o.lookHome && !this.lurking && this.homeR) want = Math.atan2(this.home[1] - py[j], this.home[0] - px[j]);
      else if (this.rects.length) {
        let best = 1e18, cx = 0, cy = 0;
        for (const q of this.rects) {
          const ddx = Math.max(q.l - px[j], 0, px[j] - q.r), ddy = Math.max(q.t - py[j], 0, py[j] - q.b);
          const d = ddx * ddx + ddy * ddy;
          if (d < best) { best = d; cx = (q.l + q.r) / 2; cy = (q.t + q.b) / 2; }
        }
        want = Math.atan2(cy - py[j], cx - px[j]);
      } else want = Math.atan2(this.hy[j], this.hx[j]);
      want *= 180 / Math.PI;
      const diff = ((want - this.ang[j] + 540) % 360) - 180;
      this.ang[j] += clamp(diff * 0.22, -9, 9);
      const cs = Math.cos((this.ang[j] * Math.PI) / 180);
      const ft = cs > 0.35 ? 1 : cs < -0.35 ? -1 : this.flip[j] >= 0 ? 1 : -1;
      this.flip[j] += clamp(ft - this.flip[j], -0.2, 0.2);
    }
  }

  // los que van y vuelven o quedaron metidos en algo se desvanecen y reaparecen en un lugar libre
  lifecycle() {
    const { px, py } = this;
    for (let j = 0; j < this.n; j++) {
      const dx = px[j] - this.lx[j], dy = py[j] - this.ly[j], mag = Math.hypot(dx, dy);
      const pm = Math.hypot(this.pdx[j], this.pdy[j]);
      const flip = dx * this.pdx[j] + dy * this.pdy[j] < -0.3 * mag * pm && mag > 1.5 && pm > 1.5;
      this.rev[j] = this.rev[j] * 0.88 + (flip ? 1 : 0);
      this.pdx[j] = dx; this.pdy[j] = dy; this.lx[j] = px[j]; this.ly[j] = py[j];
      if (!this.o.lurk && this.t > 1.5 && (this.rev[j] > 2 || this.resid[j] > this.rad[j] * 0.5 + 6)) this.atgt[j] = 0;
      if (this.alpha[j] < 0.03 && this.atgt[j] === 0) {
        const s = this.freeSpot(j);
        if (s) {
          px[j] = this.ppx[j] = this.lx[j] = s[0]; py[j] = this.ppy[j] = this.ly[j] = s[1];
          this.vx[j] = this.gauss() * 1.5; this.vy[j] = this.gauss() * 1.5;
          this.atgt[j] = 1; this.rev[j] = 0; this.pdx[j] = this.pdy[j] = 0;
        }
      }
      const goal = !this.o.lurk && this.t < this.fadeDelay[j] ? 0 : this.atgt[j];
      this.alpha[j] += clamp(goal - this.alpha[j], -1 / (0.25 * 30), 1 / (0.35 * 30));
    }
    if (this.t > this.nextGroupBlink) {             // parpadeo en grupo, en ola de izquierda a derecha
      for (let j = 0; j < this.n; j++) this.blinkAt[j] = this.t + (this.px[j] / this.W) * 0.25;
      this.nextGroupBlink = this.t + 6 + this.rng() * 4;
    }
  }

  draw(alphaInterp) {
    const ctx = this.canvas.getContext('2d');
    const S = this.S, spr = SPR_REEL * S;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.cw, this.ch);
    for (let j = 0; j < this.n; j++) {
      const al = this.alpha[j] * (this.size[j] < 0.625 ? 0.8 : 1);   // los chicos quedan un poco atrás
      if (al < 0.02) continue;
      const img = (this.red[j] ? this.sprites.red : this.sprites.ink)[this.idx[j]];
      if (!img) continue;
      const x = (this.ppx[j] + (this.px[j] - this.ppx[j]) * alphaInterp) * S;
      const y = (this.ppy[j] + (this.py[j] - this.ppy[j]) * alphaInterp) * S;
      const wig = Math.round(Math.sin(this.phase[j])) + 1;
      // parpadeo: 0.2 s cerrándose y abriéndose
      let bl = 0;
      const u = (this.t - this.blinkAt[j]) / 0.2;
      if (u >= 0 && u <= 1) bl = 3 - Math.round(Math.abs(2 * u - 1) * 3);
      else if (u > 1) this.blinkAt[j] = this.t + 2 + (-Math.log(1 - this.rng())) * 4;
      let q = Math.round(this.flip[j] * 4); if (q === 0) q = this.flip[j] >= 0 ? 1 : -1;
      const w = spr * this.size[j] * [0.93, 1, 1.07][wig];
      const h = ((spr * this.size[j] * img.height) / img.width) * BLINK[bl] * [1.05, 1, 0.95][wig] * (Math.abs(q) / 4);
      ctx.globalAlpha = al;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((this.ang[j] * Math.PI) / 180);
      if (q < 0) ctx.scale(1, -1);                  // mirando a la izquierda: se da vuelta, no queda cabeza abajo
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  advancePhases() {
    for (let j = 0; j < this.n; j++) this.phase[j] += 0.25 + Math.hypot(this.vx[j], this.vy[j]) * 0.07;
  }
}

const DEFAULTS = {
  density: 0.55, minEyes: 26, maxEyes: 190, redRatio: 0.2, orbitScale: 1,
  spriteRatio: 0.03, spriteMin: 30, spriteMax: 46, avoidPad: 10, topPad: 0, seed: 12, targetSelector: null,
  lurk: false, speed: 1, scopeSelector: null, watch: false, lookHome: false, parkSides: false,   // watch: relee el DOM al instante cuando cambia (quiz)
};

// <Cardumen /> llena a su padre (que tiene que ser position: relative).
// apiRef.current expone startle(x, y) en px relativos al padre.
export default function Cardumen({ className = '', apiRef, ...opts }) {
  const canvasRef = useRef(null);
  const optsRef = useRef({ ...DEFAULTS, ...opts });

  useEffect(() => {
    const canvas = canvasRef.current, container = canvas.parentElement;
    let school = null, raf = 0, last = 0, acc = 0, visible = true, dead = false;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const frame = (now) => {
      raf = 0;
      if (!school || dead) return;
      const dt = Math.min(0.1, (now - (last || now)) / 1000); last = now;
      acc += dt;
      while (acc >= STEP) { school.step(); school.advancePhases(); acc -= STEP; }
      school.draw(acc / STEP);
      if (visible && !document.hidden) raf = requestAnimationFrame(frame);
    };
    const kick = () => { if (!raf && visible && !reduced && !document.hidden) { last = 0; raf = requestAnimationFrame(frame); } };

    loadSprites().then((sprites) => {
      if (dead || !sprites.ink.length) return;
      school = new School(canvas, container, optsRef.current, sprites);
      if (apiRef) apiRef.current = {
        startle: (x, y) => { school.startle(x, y); kick(); },
        refresh: () => school.readRects(),
      };
      if (reduced) {                                 // sin movimiento: un cuadro quieto, ya acomodado
        for (let k = 0; k < 90; k++) school.step();
        school.alpha.fill(1); school.draw(1);
      } else kick();
    });

    const ro = new ResizeObserver(() => {
      if (!school) return;
      school.resize();
      if (reduced) { for (let k = 0; k < 60; k++) school.step(); school.alpha.fill(1); school.draw(1); }
    });
    ro.observe(container);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible) kick(); }, { rootMargin: '100px' });
    io.observe(container);
    const rectTimer = setInterval(() => school && school.readRects(), 800);   // fuentes que cargan, acordeones, etc.
    // watch: el quiz cambia de opción o de pregunta, o se scrollea: releer ya, no en 800 ms
    let reread = 0, mo = null;
    const soon = () => { if (reread) return; reread = requestAnimationFrame(() => { reread = 0; if (school) { school.readRects(); kick(); } }); };
    if (optsRef.current.watch) {
      mo = new MutationObserver(soon);
      mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-selected', 'data-quiz-focus', 'data-avoid'] });
      document.addEventListener('scroll', soon, true);
    }

    const local = (e) => { const b = container.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };
    // una sola vez por visita: avisa que alguien jugó con los ojos (clic o un rato moviendo el cursor)
    let played = false, moves = 0;
    const ping = () => { if (!played && optsRef.current.onInteract) { played = true; optsRef.current.onInteract(); } };
    const onMove = (e) => { if (school && e.pointerType === 'mouse') { school.setPointer(...local(e)); if (++moves > 40) ping(); } };
    const onLeave = () => { if (school) school.pointer = null; };
    const onDown = (e) => {
      if (!school || e.target.closest('a,button,input,label')) return;
      school.startle(...local(e)); kick(); ping();
    };
    const onVis = () => kick();
    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);
    container.addEventListener('pointerdown', onDown);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      dead = true;
      cancelAnimationFrame(raf);
      ro.disconnect(); io.disconnect(); clearInterval(rectTimer);
      if (mo) { mo.disconnect(); document.removeEventListener('scroll', soon, true); }
      cancelAnimationFrame(reread);
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
      container.removeEventListener('pointerdown', onDown);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [apiRef]);

  return <canvas ref={canvasRef} aria-hidden="true" className={`pointer-events-none absolute inset-0 ${className}`} />;
}
